// src/components/shop/ShopPageProduct.jsx

import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useSelector, useDispatch } from 'react-redux';

import PageHeader from '../shared/PageHeader';
import Product from '../shared/Product';
import ProductTabs from './ProductTabs';
import { getProduct } from '../../store/product';

import BlockProductsCarousel from '../blocks/BlockProductsCarousel';
import WidgetCategories from '../widgets/WidgetCategories';
import WidgetProducts from '../widgets/WidgetProducts';

import categories from '../../data/shopWidgetCategories';
import theme from '../../data/theme';

function ShopPageProduct({ layout, sidebarPosition }) {
  const { productId } = useParams();
  const dispatch = useDispatch();

  // Все товары категории (для карусели «Другие товары»)
  const products = useSelector(state => state.shop.products);
  // Загруженный единичный товар
  const fetchedProduct = useSelector(state => state.productCard.product);
  // До появления fetchedProduct можно попытаться найти товар среди уже загруженных
  const product =
    fetchedProduct ||
    products.find(p => p.id === productId);

  // Скроллим наверх при смене productId
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productId]);

  // Запрос на сервер при монтировании и при смене productId
  useEffect(() => {
    if (productId) {
      dispatch(getProduct(productId));
    }
  }, [dispatch, productId]);

  // Пока нет данных по товару, показываем лоадер
  if (!product || !product.id) {
    return (
      <div className="block">
        <div className="container">Загрузка товара…</div>
      </div>
    );
  }

  // Хлебные крошки
  const breadcrumb = [
    { title: 'Главная', url: '/' },
    {
      title: 'Каталог',
      url: `/shop/category/${product.category_id}`,
    },
    { title: product.name, url: '' },
  ];

  // Собираем основной контент: с сайдбаром или без
  let content;
  if (layout === 'sidebar') {
    const sidebar = (
      <div className="shop-layout__sidebar">
        <div className="block block-sidebar">
          <div className="block-sidebar__item">
            <WidgetCategories
              categories={categories}
              location="shop"
            />
          </div>
          <div className="block-sidebar__item d-none d-lg-block">
            <WidgetProducts
              title="Latest Products"
              products={products.slice(0, 5)}
            />
          </div>
        </div>
      </div>
    );

    content = (
      <div className="container">
        <div
          className={`shop-layout shop-layout--sidebar--${sidebarPosition}`}
        >
          {sidebarPosition === 'start' && sidebar}
          <div className="shop-layout__content">
            <div className="block">
              <Product product={product} layout={layout} />
              <ProductTabs product={product} withSidebar />
            </div>
            <BlockProductsCarousel
              title="Другие товары"
              layout="grid-4-sm"
              products={products.slice(0, 5)}
              withSidebar
            />
          </div>
          {sidebarPosition === 'end' && sidebar}
        </div>
      </div>
    );
  } else {
    content = (
      <>
        <div className="block">
          <div className="container">
            <Product product={product} layout={layout} />
            <ProductTabs product={product} />
          </div>
        </div>
        <BlockProductsCarousel
          title="Другие товары"
          layout="grid-5"
          products={products.slice(0, 6)}
        />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${product.name} — ${theme.name}`}</title>
      </Helmet>
      <PageHeader breadcrumb={breadcrumb} />
      {content}
    </>
  );
}

ShopPageProduct.propTypes = {
  layout: PropTypes.oneOf([
    'standard',
    'sidebar',
    'columnar',
    'quickview',
  ]),
  sidebarPosition: PropTypes.oneOf(['start', 'end']),
};

ShopPageProduct.defaultProps = {
  layout: 'standard',
  sidebarPosition: 'start',
};

export default ShopPageProduct;
